/**
 * Account Delete Modal Component
 *
 * Handles account deletion with confirmation modal, countdown timer, and error handling.
 */

(() => {
    function registerAccountDeleteModal() {
        Alpine.data('accountDeleteModal', () => ({
            // Modal states
            showConfirmModal: false,
            showSuccessModal: false,
            showErrorModal: false,

            // Countdown timer
            countdownSeconds: 0,
            countdownInterval: null,
            isDeleteButtonDisabled: true,

            // Loading states
            isDeleting: false,

            // Messages
            errorMessage: '',
            successMessage: '',

            // Configuration
            countdownDuration: 5, // seconds

            init() {
                // Get countdown duration from data attribute if available
                const countdownAttr = this.$el.dataset.countdown;
                if (countdownAttr) {
                    this.countdownDuration = parseInt(countdownAttr) || 5;
                }
            },

            // Open confirmation modal
            openConfirmModal() {
                this.showConfirmModal = true;
                this.startCountdown();
            },

            // Close confirmation modal
            closeConfirmModal() {
                this.showConfirmModal = false;
                this.stopCountdown();
                this.resetCountdown();
            },

            // Start countdown timer
            startCountdown() {
                this.countdownSeconds = this.countdownDuration;
                this.isDeleteButtonDisabled = true;

                this.countdownInterval = setInterval(() => {
                    this.countdownSeconds--;

                    if (this.countdownSeconds <= 0) {
                        this.stopCountdown();
                        this.isDeleteButtonDisabled = false;
                    }
                }, 1000);
            },

            // Stop countdown timer
            stopCountdown() {
                if (this.countdownInterval) {
                    clearInterval(this.countdownInterval);
                    this.countdownInterval = null;
                }
            },

            // Reset countdown
            resetCountdown() {
                this.countdownSeconds = 0;
                this.isDeleteButtonDisabled = true;
            },

            // Handle account deletion
            async deleteAccount() {
                if (this.isDeleting || this.isDeleteButtonDisabled) {
                    return;
                }

                this.isDeleting = true;

                try {
                    const response = await fetch(accountDeleteData.ajaxUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                        body: new URLSearchParams({
                            action: 'account_delete',
                            nonce: accountDeleteData.nonce,
                        }),
                    });

                    const data = await response.json();

                    if (data.success) {
                        this.successMessage =
                            data.data || 'Your account has been deleted successfully.';
                        this.closeConfirmModal();
                        this.showSuccessModal = true;
                    } else {
                        this.errorMessage =
                            data.data || 'An error occurred while deleting your account.';
                        this.closeConfirmModal();
                        this.showErrorModal = true;
                    }
                } catch (error) {
                    console.error('Account deletion error:', error);
                    this.errorMessage = 'Network error occurred. Please try again.';
                    this.closeConfirmModal();
                    this.showErrorModal = true;
                } finally {
                    this.isDeleting = false;
                }
            },

            // Handle success modal close
            closeSuccessModal() {
                this.showSuccessModal = false;
                // Redirect to home page
                window.location.href = accountDeleteData.homeUrl || '/';
            },

            // Handle error modal close
            closeErrorModal() {
                this.showErrorModal = false;
            },

            // Get countdown display text
            getCountdownText() {
                if (this.countdownSeconds <= 0) {
                    return 'Delete Account';
                }
                return `Delete Account (${this.countdownSeconds}s)`;
            },

            // Get countdown progress percentage
            getCountdownProgress() {
                const progress =
                    ((this.countdownDuration - this.countdownSeconds) / this.countdownDuration) *
                    100;
                return Math.min(progress, 100);
            },
        }));
    }

    // Register when Alpine is available
    if (typeof Alpine !== 'undefined' && Alpine.version) {
        registerAccountDeleteModal();
    } else {
        document.addEventListener('alpine:init', () => {
            registerAccountDeleteModal();
        });
    }
})();
